import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { OTPInput } from "@/components/ui/OTPInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useCreateMfaChallenge, useVerifyMfaCode } from "@/queries/auth/mfa";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums/toast";

const verifyMfaSchema = z.object({
  code: z.string().length(6, "Code must be 6 digits"),
});

type VerifyMfaFormValues = z.infer<typeof verifyMfaSchema>;

interface MFAVerificationDialogProps {
  isOpen: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const MFAVerificationDialog = ({
  isOpen,
  onSuccess,
  onCancel,
}: MFAVerificationDialogProps) => {
  const { mutate: createChallenge, isPending: isCreatingChallenge } = useCreateMfaChallenge();
  const { mutate: verifyMfaCode, isPending: isVerifyingCode } = useVerifyMfaCode();
  const [challengeId, setChallengeId] = useState<string | null>(null);

  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm<VerifyMfaFormValues>({
    resolver: zodResolver(verifyMfaSchema),
    defaultValues: {
      code: "",
    },
    mode: "onSubmit",
  });

  const isLoading = isCreatingChallenge || isVerifyingCode;

  useEffect(() => {
    if (isOpen && !challengeId) {
      createChallenge(undefined, {
        onSuccess: response => {
          setChallengeId(response.challenge_id);
        },
        onError: () => {
          displayErrorToast("Failed to create MFA challenge");
          onCancel();
        },
      });
    }
  }, [isOpen, challengeId, createChallenge, onCancel]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setChallengeId(null);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (isOpen) {
      const inputElement = document.querySelector('[data-tid="verify-email-input-0"]');
      if (inputElement) {
        (inputElement as HTMLInputElement).focus();
      }
    }
  }, [isOpen]);

  const onSubmit = (data: VerifyMfaFormValues) => {
    if (!challengeId) {
      displayErrorToast("Challenge not initialized. Please try again.");
      return;
    }

    verifyMfaCode(
      {
        code: data.code,
        challenge_id: challengeId,
      },
      {
        onSuccess: ({ valid }) => {
          if (valid) {
            displayToast("MFA verified successfully", ToastType.success);
            onSuccess();
          } else {
            displayErrorToast("Invalid MFA code. Please try again.");
            reset();
          }
        },
        onError: () => {
          reset();
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Verify MFA</DialogTitle>
          <DialogDescription>
            Please enter the verification code from your authenticator app.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <OTPInput
            code={watch("code")}
            setCode={value =>
              setValue("code", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            onPaste={value => onSubmit({ code: value })}
            errorMessage={errors.code?.message}
          />

          <DialogFooter className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isLoading || !challengeId}>
              {isLoading ? <LoadingSpinner size={4} /> : "Verify"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
